import React, { useRef, useState } from 'react'
import { Formik } from 'formik'
import classNames from 'classnames'
import { useParams } from 'react-router-dom'
import { useNotificationService } from '@abroad/components'
import { Form as RBForm } from 'react-bootstrap'
import Imgix from 'react-imgix'
import PropTypes from 'prop-types'
import ImageCropModal from '../common/ImageCropModal'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const HEADER_ASPECT = 2 / 1
const HEADER_RATIO_LABEL = '2:1'

const HeaderImage = ({
  questDetail,
  addUpdateQuest,
  isFormSubmitting,
  uploadImage,
}) => {
  const imageRef = useRef(null)
  const { questId } = useParams()
  const Notification = useNotificationService()
  const [questImageUpload, setQuestImageUpload] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [isDraggingImage, setIsDraggingImage] = useState(false)
  const [showRemoveImageConfirm, setShowRemoveImageConfirm] = useState(false)
  const pendingRemoveRef = useRef(null)
  const inputResetRef = useRef(null)

  const getImage = () => {
    if (imageRef.current) {
      imageRef.current.value = ''
      imageRef.current.click()
    }
  }

  const validateImageFile = (imageFile) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = imageFile?.name?.split('.').pop()?.toUpperCase()

    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      return false
    }

    if (
      imageFile.type === 'image/webp' ||
      imageFile.name.toLowerCase().endsWith('.webp')
    ) {
      Notification.showNotification(
        'WebP format is not supported. Please use JPG, JPEG, or PNG format.',
        'danger',
      )
      return false
    }
    return true
  }

  const openCropModal = (imageFile, resetInput) => {
    if (!imageFile) return
    if (!validateImageFile(imageFile)) {
      if (resetInput) resetInput()
      return
    }
    inputResetRef.current = resetInput || null
    setSelectedImageFile(imageFile)
    setShowImageCropModal(true)
  }

  const resetImageInput = () => {
    if (typeof inputResetRef.current === 'function') {
      inputResetRef.current()
    }
    inputResetRef.current = null
  }

  const handleCropModalClose = () => {
    setShowImageCropModal(false)
    setSelectedImageFile(null)
    setIsDraggingImage(false)
    resetImageInput()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(true)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDraggingImage) setIsDraggingImage(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDraggingImage(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDraggingImage(false)
    const files = e.dataTransfer?.files
    if (files && files.length > 0) {
      openCropModal(files[0])
      e.dataTransfer.clearData()
    }
  }

  const uploadQuestImage = async (imageFile) => {
    try {
      setQuestImageUpload(true)
      const fileName = await uploadImage(imageFile, 'imageFileName')
      return fileName
    } catch (e) {
      console.error(e)
      throw e
    } finally {
      setTimeout(() => {
        setQuestImageUpload(false)
      }, 850)
    }
  }

  const handleRemoveImage = async (setFieldValue) => {
    try {
      setIsRemoving(true)
      setFieldValue('imageFileName', '')

      if (typeof addUpdateQuest === 'function') {
        await addUpdateQuest({ imageFileName: '', id: questId })
      } else {
        Notification.showNotification('Failed to remove image', 'danger')
      }
    } catch (e) {
      throw e
    } finally {
      setTimeout(() => {
        setIsRemoving(false)
      }, 300)
    }
  }

  // Imgix path matches slider / faculty / client: /quest/{fileName} (no questId).
  const imageSrc = (fileName) => {
    if (!fileName) return ''
    if (fileName.startsWith('data:')) return fileName
    return `${process.env.REACT_APP_IMG_SOURCE}/quest/${fileName}`
  }

  const miniStyle = (fileName) => {
    if (!fileName || fileName.startsWith('data:')) {
      return fileName ? { backgroundImage: `url(${fileName})` } : undefined
    }
    return {
      backgroundImage: `url(${imageSrc(
        fileName,
      )}?ar=3:2&fit=crop&crop=center&w=88&auto=format)`,
    }
  }

  return (
    <Formik
      initialValues={{
        imageFileName:
          questDetail?.imageFileName || questDetail?.questImage || '',
      }}
      enableReinitialize
      onSubmit={(values) => {
        addUpdateQuest(values)
      }}>
      {({ handleSubmit, values, setFieldValue }) => {
        const hasImage = !!values?.imageFileName
        const busy = questImageUpload || isRemoving

        return (
          <form onSubmit={handleSubmit} aria-busy={busy || undefined}>
            <div className='experience-cover'>
              <div className='experience-img-slot' data-ratio='2:1'>
                <div
                  className={classNames('experience-img-frame', {
                    'is-empty': !hasImage && !busy,
                    'is-dragging': isDraggingImage && !busy,
                  })}
                  onClick={() => {
                    if (!busy) getImage()
                  }}
                  onDragEnter={(e) => {
                    if (!busy) handleDragEnter(e)
                  }}
                  onDragOver={(e) => {
                    if (!busy) handleDragOver(e)
                  }}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => {
                    if (!busy) handleDrop(e)
                  }}
                  role='button'
                  tabIndex={busy ? -1 : 0}
                  aria-disabled={busy || undefined}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      if (!busy) getImage()
                    }
                  }}>
                  {busy ? (
                    <span
                      className='experience-img-hover'
                      style={{ opacity: 1 }}>
                      {isRemoving ? 'Removing…' : 'Uploading…'}
                    </span>
                  ) : (
                    <span className='experience-img-hover'>
                      {isDraggingImage
                        ? 'Drop image to upload'
                        : hasImage
                        ? 'Click to replace'
                        : 'Upload image'}
                    </span>
                  )}

                  {hasImage ? (
                    values.imageFileName.startsWith('data:') ? (
                      <img src={values.imageFileName} alt='Experience header' />
                    ) : (
                      <Imgix
                        imgixParams={{
                          fit: 'crop',
                          crop: 'center',
                          ar: HEADER_RATIO_LABEL,
                        }}
                        src={imageSrc(values.imageFileName)}
                        attributeConfig={{
                          src: 'data-src',
                          srcSet: 'data-srcset',
                          sizes: 'data-sizes',
                        }}
                        htmlAttributes={{
                          src: `${imageSrc(
                            values.imageFileName,
                          )}?ar=${HEADER_RATIO_LABEL}&fit=crop&crop=center&fill=blur&blur=300&px=16&auto=format`,
                        }}
                        width={'100%'}
                        alt='Experience header'
                        className={classNames('lazyload', {
                          itenaryImage: questImageUpload,
                        })}
                      />
                    )
                  ) : !busy ? (
                    <>
                      <b>Upload image</b>
                      <small>JPG, JPEG or PNG · cropped to 2:1</small>
                    </>
                  ) : null}

                  {hasImage && !busy ? (
                    <button
                      type='button'
                      className='experience-img-remove'
                      aria-label='Remove image'
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        pendingRemoveRef.current = () =>
                          handleRemoveImage(setFieldValue)
                        setShowRemoveImageConfirm(true)
                      }}>
                      ×
                    </button>
                  ) : null}

                  <RBForm.Control
                    type='file'
                    size='lg'
                    placeholder='Select File'
                    accept='image/jpg, image/jpeg, image/png'
                    onChange={async (e) => {
                      const target = e.target
                      const files = e.target.files
                      if (files && files?.length > 0) {
                        openCropModal(files[0], () => {
                          if (target) target.value = ''
                        })
                      }
                    }}
                    style={{ display: 'none' }}
                    name='imageFileName'
                    sm='10'
                    ref={imageRef}
                  />
                </div>
              </div>

              <div className='experience-img-uses'>
                <div className='experience-img-use'>
                  <span
                    className='experience-img-mini'
                    style={miniStyle(values.imageFileName)}
                  />
                  <div>
                    <b>Experiences list</b>
                    <span>The thumbnail beside the name in the admin</span>
                  </div>
                </div>
                <div className='experience-img-use'>
                  <span
                    className='experience-img-mini'
                    style={miniStyle(values.imageFileName)}
                  />
                  <div>
                    <b>Participant view</b>
                    <span>
                      The still image at the top of Your Experiences — it
                      doesn&apos;t rotate here
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <ImageCropModal
              show={showImageCropModal}
              onHide={handleCropModalClose}
              file={selectedImageFile}
              onConfirm={async (croppedImageFile) => {
                if (!croppedImageFile) return
                setShowImageCropModal(false)
                setQuestImageUpload(true)
                const reader = new FileReader()
                reader.onloadend = async () => {
                  setFieldValue(`imageFileName`, reader.result)
                  try {
                    const fileName = await uploadQuestImage(croppedImageFile)
                    setFieldValue(`imageFileName`, fileName)
                  } catch (e) {
                    Notification.showNotification(
                      'Image upload failed',
                      'danger',
                    )
                  } finally {
                    setSelectedImageFile(null)
                    setIsDraggingImage(false)
                    resetImageInput()
                  }
                }
                reader.readAsDataURL(croppedImageFile)
              }}
              isUploading={questImageUpload}
              aspect={HEADER_ASPECT}
              ratioLabel={HEADER_RATIO_LABEL}
              title='Crop Image'
            />
            <ExperienceConfirmModal
              show={showRemoveImageConfirm}
              onHide={() => {
                if (!isRemoving) {
                  setShowRemoveImageConfirm(false)
                  pendingRemoveRef.current = null
                }
              }}
              onConfirm={async () => {
                const run = pendingRemoveRef.current
                pendingRemoveRef.current = null
                setShowRemoveImageConfirm(false)
                if (run) await run()
              }}
              title='Remove this image?'
              message='The image will be cleared. You can upload another.'
              confirmLabel='Delete'
              isLoading={isRemoving}
            />
          </form>
        )
      }}
    </Formik>
  )
}

HeaderImage.propTypes = {
  questDetail: PropTypes.shape({
    imageFileName: PropTypes.string,
  }),
  addUpdateQuest: PropTypes.func.isRequired,
  isFormSubmitting: PropTypes.bool,
}

export default HeaderImage
